import * as Sentry from '@sentry/vue';
import { BaseModel } from './BaseModel';
import { sb } from '../plugins/sendbird';
import { Learner, LearningUnit, Message, Section, SendbirdUser, Teacher } from '.';

const ERROR_CODE = {
  USER_NOT_MEMBER: 900020
};
export class Channel extends BaseModel {
  static get entity() {
    return 'channels';
  }

  static get primaryKey() {
    return 'url';
  }

  static get fk() {
    return 'channel_id';
  }

  static fields() {
    return {
      channelType: this.attr(null),
      coverUrl: this.attr(null),
      createdAt: this.attr(null),
      creator: this.attr(null),
      customType: this.attr(null),
      data: this.attr(null),
      hiddenState: this.attr(null),
      invitedAt: this.attr(null),
      inviter: this.attr(null),
      isAccessCodeRequired: this.attr(null),
      isBroadcast: this.attr(null),
      isDiscoverable: this.attr(null),
      isDistinct: this.attr(null),
      isEphemeral: this.attr(null),
      isFrozen: this.attr(null),
      isHidden: this.attr(null),
      isPublic: this.attr(null),
      isPushEnabled: this.attr(null),
      isSuper: this.attr(null),
      joinedAt: this.attr(null),
      joinedMemberCount: this.attr(null),
      lastMessage: this.attr(null),
      memberCount: this.attr(null),
      memberMap: this.attr(null),
      members: this.attr(null),
      messageSurvivalSeconds: this.attr(null),
      myCountPreference: this.attr(null),
      myLastRead: this.attr(null),
      myMemberState: this.attr(null),
      myMutedState: this.attr(null),
      myPushTriggerOption: this.attr(null),
      myRole: this.attr(null),
      name: this.attr(null),
      unreadMentionCount: this.attr(null),
      unreadMessageCount: this.attr(null),
      url: this.attr(null),
      metadata: this.attr(null),
      messageListQuery: this.attr(null)
    };
  }

  static get globalApiConfig() {
    return {
      baseURL: `https://api-${process.env.VUE_APP_SENDBIRD_APP_ID}.sendbird.com/v3`,
      headers: {
        'Api-Token': process.env.VUE_APP_SENDBIRD_API_TOKEN
      }
    };
  }

  static getJoinedChannel(userId, checkedMetadata = null) {
    return this.query()
      .where(({ members = [], metadata = null } = {}) => {
        let isMember = members.some((member) => member.userId == userId);

        let isMatchMetadata = true;

        if (checkedMetadata && metadata) {
          for (const key in checkedMetadata) {
            if (Object.hasOwnProperty.call(checkedMetadata, key)) {
              const checkedValue = checkedMetadata[key];

              isMatchMetadata = metadata[key] == checkedValue;
            }
          }
        }

        return isMember && isMatchMetadata;
      })
      .get();
  }

  static findChannel(channelUrl) {
    return this.find(channelUrl);
  }

  static findChannelIn(channelUrls) {
    return this.query().whereIdIn(channelUrls).get();
  }

  static isNotMemberError({ code } = {}) {
    return code && code === ERROR_CODE.USER_NOT_MEMBER;
  }

  static async isLearnerJoined(channelUrl, learnerId) {
    const groupChannel = await sb.GroupChannel.getChannel(channelUrl);

    const { memberMap = {} } = groupChannel;

    const isLearnerJoined = memberMap[learnerId] !== undefined;

    return isLearnerJoined;
  }

  static registerEventHandlers(key) {
    Channel.createEventHandlers(`globalListener-${key}`, {
      onMessageReceived: async (channel, message) => {
        // console.log(channel, message);
        if (message.messageType !== 'admin') {
          // const metaData = await channel.getMetaData(["learning_unit", "section", "learner", "teachers", `${key}`]);

          // if (metaData[key]) {
          //   Channel.insertOrUpdate({ data: { ...channel, metadata: metaData } });

          //   Message.insert({ data: message });

          //   SendbirdUser.getTotalUnreadMessageCount(key);
          // }

          // Channel.insertOrUpdate({ data: { ...channel, metadata: metaData } });

          Message.insert({ data: message });

          SendbirdUser.getTotalUnreadMessageCount(key);
        }
      },

      onMessageUpdated: async (channel, message) => {
        const metaData = await channel.getMetaData([
          'learning_unit',
          'section',
          'learner',
          'teachers',
          `${key}`
        ]);

        Channel.update({ data: { ...channel, metadata: metaData } });

        Message.update({ data: message });

        SendbirdUser.getTotalUnreadMessageCount(key);
      },

      onMessageDeleted: async (channel, messageId) => {
        Channel.update({ data: { ...channel } });

        Message.delete(messageId);

        SendbirdUser.getTotalUnreadMessageCount(key);
      },

      onChannelChanged: async (channel) => {
        const metaData = await channel.getMetaData([
          'learning_unit',
          'section',
          'learner',
          'teachers',
          `${key}`
        ]);

        if (metaData[key]) {
          Channel.insertOrUpdate({ data: { ...channel, metadata: metaData } });

          // SendbirdUser.getTotalUnreadMessageCount(key);
        }
      },

      onMetaDataCreated: async (channel, metaData) => {
        // console.log(channel, metaData);

        const existedChannel = Channel.find(channel.url);

        // console.log(metaData[key] || existedChannel !== null);
        if (metaData[key] || existedChannel !== null) {
          const channelMetaData = await channel.getMetaData([
            'learning_unit',
            'section',
            'learner',
            'teachers',
            `${key}`
          ]);

          Channel.insertOrUpdate({
            data: { ...channel, metadata: channelMetaData }
          });
        }
      },

      // onMetaDataUpdated: async (channel, metaData) => {
      //   console.log(channel, metaData);
      // },

      onMetaDataDeleted: async (channel, metaData) => {
        // console.log(channel, metaData);

        if (metaData.indexOf(`${key}`) !== -1) {
          await channel.markAsRead();

          await SendbirdUser.getTotalUnreadMessageCount(key);

          Channel.delete(channel.url);
        } else {
          const existedChannel = Channel.find(channel.url);

          if (existedChannel !== null) {
            const channelMetaData = await channel.getMetaData([
              'learning_unit',
              'section',
              'learner',
              'teachers',
              `${key}`
            ]);

            Channel.insertOrUpdate({
              data: { ...channel, metadata: channelMetaData }
            });
          }
        }
      },

      onUserJoined: async (channel, user) => {
        // console.log(channel, user);
        const existedChannel = Channel.find(channel.url);

        if (user.userId == key || existedChannel !== null) {
          const metaData = await channel.getMetaData([
            'learning_unit',
            'section',
            'learner',
            'teachers',
            `${key}`
          ]);

          const { learning_unit, section, learner } = metaData;

          await Promise.all([
            LearningUnit.api().fetch({ id: learning_unit }),
            Section.api().fetch({ id: section }),
            Learner.api().fetch({ id: learner })
          ]);

          Channel.insertOrUpdate({ data: { ...channel, metadata: metaData } });
        }
      }
    });
  }

  static createEventHandlers(key, eventHandlers = {}) {
    sb.removeChannelHandler(key);

    const channelHandler = new sb.ChannelHandler();

    for (const eventHandler in eventHandlers) {
      if (Object.hasOwnProperty.call(eventHandlers, eventHandler)) {
        channelHandler[eventHandler] = eventHandlers[eventHandler];
      }
    }

    sb.addChannelHandler(key, channelHandler);

    return channelHandler;
  }

  static removeEventHandler(key) {
    sb.removeChannelHandler(key);
  }

  static getTotalUnreadChannelCount() {
    return sb.getTotalUnreadChannelCount(function (count, error) {
      if (error) {
        // Handle error.
        Sentry.captureException(error);
      }
      return count;
    });
  }

  isLearner(userId) {
    return this.metadata && this.metadata.learner == userId;
  }

  learner() {
    let learnerData = null;

    if (this.metadata) {
      let { learner = '' } = this.metadata;

      if (learner.length) {
        learnerData = Learner.find(learner);
      }
    }

    return learnerData;
  }

  teachers() {
    let teacherData = [];

    if (this.metadata) {
      let { teachers = '' } = this.metadata;

      if (teachers.length) {
        const teacherIds = teachers.split(',');

        teacherData = teacherIds.map((teacherId) => Teacher.find(teacherId)).filter(Boolean);
      }
    }

    return teacherData;
  }

  allMembers() {
    return [...this.teachers(), this.learner()].filter(Boolean);
  }

  async sendMessage(message) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    const params = new sb.UserMessageParams();

    params.message = message;
    params.customType = 'DIRECT_MESSAGE';

    return new Promise((resolve, reject) => {
      groupChannel.sendUserMessage(params, (message, error) => {
        if (error) {
          Sentry.captureException(error);

          reject('Cannot send message, please try again');
        }

        Message.insert({ data: message });

        resolve(message);
      });
    });
  }

  async updateMessage(messageId, message) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    const params = new sb.UserMessageParams();

    params.message = message;

    return new Promise((resolve, reject) => {
      groupChannel.updateUserMessage(messageId, params, (message, error) => {
        if (error) {
          Sentry.captureException(error);

          reject('Cannot update message, please try again');
        }

        Message.update({ where: messageId, data: message });

        resolve(message);
      });
    });
  }

  async deleteMessage(messageId) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    const params = new sb.MessageRetrievalParams();

    params.messageId = messageId;
    params.channelType = this.channelType;
    params.channelUrl = this.url;

    return new Promise((resolve, reject) => {
      sb.BaseMessage.getMessage(params, (message, error) => {
        if (error) {
          Sentry.captureException(error);

          reject('Cannot get message to delete, please try again');
        }

        groupChannel.deleteMessage(message, (response, error) => {
          if (error) {
            Sentry.captureException(error);

            reject('Cannot delete message, please try again');
          }

          Message.delete(messageId);

          resolve(response);
        });
      });
    });
  }

  async sendFiles(files) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    const fileParams = files.map((file) => {
      const params = new sb.FileMessageParams();

      params.file = file;
      params.fileName = file.name;
      params.fileSize = file.size;
      params.mimeType = file.type;
      params.customType = 'DIRECT_MESSAGE';

      return params;
    });

    return new Promise((resolve, reject) => {
      const messages = [];

      groupChannel.sendFileMessages(fileParams, {
        // progress: (event, messageRequestId) => {
        //   console.log(event, messageRequestId);
        // },
        sent: (message, error) => {
          if (error) {
            Sentry.captureException(error);

            reject('Cannot send file, please try again');
          }

          Message.insert({ data: message });

          messages.push(message);
        },
        complete: (error) => {
          if (error) {
            Sentry.captureException(error);

            reject('Cannot send all file successfully, please try again');
          }

          resolve(messages);
        }
      });
    });
  }

  async updateFile(messageId, file) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    const params = new sb.FileMessageParams();

    params.file = file;
    params.fileName = file.name;
    params.fileSize = file.size;
    params.mimeType = file.type;
    params.customType = 'DIRECT_MESSAGE';

    return new Promise((resolve, reject) => {
      groupChannel.updateFileMessage(messageId, params, (message, error) => {
        if (error) {
          Sentry.captureException(error);

          reject('Cannot update file, please try again');
        }

        Message.update({ where: messageId, data: message });

        resolve(message);
      });
    });
  }

  get hasMoreMessages() {
    return this.messageListQuery ? this.messageListQuery.hasMore : false;
  }

  async loadMessages({ limit = 30 } = {}) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    if (!this.messageListQuery) {
      const messageListQuery = groupChannel.createPreviousMessageListQuery();

      messageListQuery.limit = limit;
      messageListQuery.customTypeFilter = 'DIRECT_MESSAGE';
      messageListQuery.reverse = true;
      messageListQuery.includeMetaArray = true;

      this.messageListQuery = messageListQuery;
    }

    return new Promise((resolve, reject) => {
      this.messageListQuery.load((messages, error) => {
        if (error) {
          Sentry.captureException(error);

          reject('Cannot load messages, please try again');
        }

        if (messages && messages.length) {
          const insertedMessages = messages.map((message) => {
            return {
              ...message,
              url: message.url
            };
          });

          Message.insert({ data: insertedMessages });
        }

        resolve(messages);
      });
    });
  }

  async messagesAsRead(userId) {
    const groupChannel = await sb.GroupChannel.getChannel(this.url);

    await groupChannel.markAsRead();

    SendbirdUser.getTotalUnreadMessageCount(userId);
  }

  static apiConfig = {
    actions: {
      findAll() {
        return this.get(`/group_channels`);
      },
      findByUrl(channel_url = '') {
        return this.get(`/group_channels/${channel_url}`);
      },
      findMemberByUrl(channel_url = '') {
        return this.get(`/group_channels/${channel_url}/members`);
      },
      create(body) {
        return this.post(`/group_channels`, body);
      },
      update(channel_url = '', body) {
        return this.put(`/group_channels/${channel_url}`, body);
      },
      joinChannel(channel_url = '', body) {
        return this.put(`/group_channels/${channel_url}/join`, body);
      },
      leaveChannel(channel_url = '', body) {
        return this.put(`/group_channels/${channel_url}/leave`, body);
      },

      createChannelWithMetaData(
        learningUnitId = '',
        sectionId = '',
        teacherIds = [],
        learnerId = ''
      ) {
        const userIds = new Array(learnerId, ...teacherIds).map((id) => `${id}`);
        var params = new sb.GroupChannelParams();
        params.isPublic = true;
        params.isEphemeral = false;
        params.isDistinct = false;
        params.isSuper = false;
        params.addUserIds(userIds);
        params.name = `direct_message_${learningUnitId}_${sectionId}_${learnerId}`;
        params.channelUrl = `direct_message_${learningUnitId}_${sectionId}_${learnerId}`;

        return sb.GroupChannel.createChannel(params, function (groupChannel, error) {
          if (error) {
            // Handle error.
            Sentry.captureException(error);
            throw 'Cannot create direct message channel, please contact support team';
          }

          let property = {
            learning_unit: `${learningUnitId}`,
            section: `${sectionId}`,
            learner: `${learnerId}`,
            teachers: `${teacherIds}`,
            [learnerId]: 'learner'
          };

          if (teacherIds.length > 0) {
            teacherIds.forEach((element) => {
              property[element] = 'teacher';
            });
          }

          if (groupChannel) {
            groupChannel.createMetaData(property);

            Channel.insertOrUpdate({
              data: { id: groupChannel.name, ...groupChannel }
            });
          }

          return groupChannel;
        });
      },

      async getChannelListBykey(key = '', values = [], includeEmpty = true, limit = 30) {
        var listQuery = sb.GroupChannel.createPublicGroupChannelListQuery();

        listQuery.includeEmpty = includeEmpty;
        listQuery.metadataKey = key;
        listQuery.metadataValues = values;
        listQuery.limit = limit;

        return listQuery;
      },

      getChannelListBylearningUnitIds(learningUnitIds = []) {
        const listQuery = sb.GroupChannel.createPublicGroupChannelListQuery();

        listQuery.includeEmpty = true;
        listQuery.metadataKey = 'learning_unit';
        listQuery.metadataValues = learningUnitIds.map((id) => `${id}`);

        return listQuery.next(async (groupChannels, error) => {
          if (error) {
            Sentry.captureException(error);

            throw 'Cannot load channels, please try again';
          }

          const result = [];

          for (let index = 0; index < groupChannels.length; index++) {
            const channel = groupChannels[index];

            const metaData = await channel.getMetaData([
              'learning_unit',
              'section',
              'learner',
              'teachers'
            ]);

            Channel.insertOrUpdate({
              data: { ...channel, metadata: metaData }
            });

            result.push({ ...channel, metadata: metaData });
          }

          return result;
        });
      },

      getSectionDirectMessageChannels(learningUnitId, sectionId, limit = 30) {
        const listQuery = sb.GroupChannel.createPublicGroupChannelListQuery();

        listQuery.limit = limit;
        listQuery.includeEmpty = true;
        listQuery.channelNameContainsFilter = `direct_message_${learningUnitId}_${sectionId}`;

        return listQuery;
      },

      getChannel(learningUnitId, userId, sectionId) {
        return sb.GroupChannel.getChannel(
          `direct_message_${learningUnitId}_${sectionId}_${userId}`,
          function (groupChannel, error) {
            if (error) {
              Sentry.captureException(error);

              throw 'Cannot get channel, please try again';
            }

            if (groupChannel) {
              Channel.insertOrUpdate({ data: groupChannel });
            }

            return groupChannel;
          }
        );
      },

      async joinAsLearner(url, learnerId) {
        let promise;

        const groupChannel = await sb.GroupChannel.getChannel(url);

        promise = await groupChannel.join();

        const metadata = await groupChannel.getMetaData([
          'learning_unit',
          'section',
          'learner',
          'teachers'
        ]);

        if (metadata) {
          const body = {
            [learnerId]: 'learner',
            learner: `${learnerId}`
          };

          await groupChannel.updateMetaData(body, true);
        }

        return promise;
      },

      async inviteLearner(url, learnerId) {
        let promise;

        const groupChannel = await sb.GroupChannel.getChannel(url);

        promise = await groupChannel.inviteWithUserIds([`${learnerId}`]);

        const metadata = await groupChannel.getMetaData([
          'learning_unit',
          'section',
          'learner',
          'teachers'
        ]);

        if (metadata) {
          const body = {
            [learnerId]: 'learner',
            learner: `${learnerId}`
          };

          await groupChannel.updateMetaData(body, true);
        }

        return promise;
      },

      async inviteInstructor(url, userIds = ['']) {
        const modelChannel = Channel.query().where('url', url).first();

        const groupChannel = await sb.GroupChannel.getChannel(url);

        let metadata = modelChannel.metadata;

        if (metadata) {
          if (userIds && userIds.length > 0) {
            userIds = userIds.map((userId) => `${userId}`);

            let teachers = metadata.teachers;

            let body = {};

            for (let index = 0; index < userIds.length; index++) {
              const userId = userIds[index];

              teachers = teachers.concat(`,${userId}`);

              body[`${userId}`] = 'teacher';
            }

            body.teachers = teachers;

            const upsertIfNotExist = true;

            await Promise.all([
              groupChannel.inviteWithUserIds(userIds),
              groupChannel.updateMetaData(body, upsertIfNotExist)
            ]);
          }
        }
      },

      async deleteInstructor(url, userId = '') {
        const modelChannel = Channel.query().where('url', url).first();

        const groupChannel = await sb.GroupChannel.getChannel(url);

        let metadata = modelChannel.metadata;

        console.log(userId, groupChannel.memberMap);

        if (metadata) {
          if (userId) {
            let { teachers } = metadata;

            let teacherIds = teachers.split(',');

            const matchedTeacherIdIndex = teacherIds.indexOf(`${userId}`);

            if (matchedTeacherIdIndex !== -1) {
              teacherIds.splice(matchedTeacherIdIndex, 1);
            }

            teacherIds = teacherIds.join(',');

            let body = {
              teachers: teacherIds
            };

            const upsertIfNotExist = true;

            await Promise.all([
              groupChannel.updateMetaData(body, upsertIfNotExist),
              groupChannel.deleteMetaData(`${userId}`)
            ]);

            console.log('Leave channel');

            groupChannel.leave((response, err) => {
              console.log(response, err);
            });
          }
        }
      }
    }
  };
}

export default Channel;
